import { useMemo } from 'react';

import { useBizyNavigationOverride } from '../bizy-navigation-context';
import { useBizyContextualInsertStore } from '../store/use-bizy-contextual-insert-store';
import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizyGlobalChat } from './use-bizy-global-chat';

interface UseBizySidePanelChatOptions {
	model?: string;
}

export function useBizySidePanelChat(options: UseBizySidePanelChatOptions = {}) {
	const context = useBizyPageContextStore((state) => state.context);
	const pendingRequest = useBizyExternalOpenStore((state) => state.pendingRequest);
	const pendingContextualInsert = useBizyContextualInsertStore((state) => state.pendingContext);
	const navigationOverride = useBizyNavigationOverride();

	const extraContext = useMemo(() => {
		const baseContext: Record<string, string> = {
			bizy_page_name: context.pageName,
			bizy_page_type: context.pageType,
			...(context.pageData || {}),
		};

		// When the panel was opened via bizy:open (e.g. "Request a feature" button),
		// attach the entry point source to the API context. The backend saves source
		// on ChatHistory and the system prompt uses it to tailor behavior (e.g.
		// feature request conversations collect feedback instead of troubleshooting).
		// Only present for the first message — cleared after auto-send — but
		// conversation history carries the intent forward.
		// The page context is already covered by bizy_page_name/bizy_page_type above.
		if (pendingRequest) {
			baseContext.source = pendingRequest.source;
			baseContext.source_page_path = pendingRequest.pagePath;
		}

		// Contextual `/` invocation from a supported text input (recognition give
		// box, comment reply). Carries the surface + surfaceData so the backend
		// can tailor Bizy's drafting behavior. The surface is also used as
		// `source` so `ChatHistory` records the entry point.
		if (pendingContextualInsert) {
			baseContext.source = `contextual_${pendingContextualInsert.surface}`;
			baseContext.bizy_contextual_surface = pendingContextualInsert.surface;
			if (pendingContextualInsert.recipientLabel) {
				baseContext.bizy_contextual_recipientLabel = pendingContextualInsert.recipientLabel;
			}
			for (const [key, value] of Object.entries(pendingContextualInsert.surfaceData)) {
				if (value === null || value === undefined) continue;
				baseContext[`bizy_contextual_${key}`] = Array.isArray(value) ? value.join(',') : String(value);
			}
		}

		return baseContext;
	}, [context.pageData, context.pageName, context.pageType, pendingRequest, pendingContextualInsert]);

	return useBizyGlobalChat({
		extraContext,
		model: options.model,
		navigateFn: navigationOverride ?? undefined,
	});
}
